import { RootState } from '@/store';
import { createAction, createAsyncThunk } from '@reduxjs/toolkit';
import { getStoredWorkspaceId } from '@/shared/utils/workspace';
import type {
  Conversa,
  DirectionPendencia,
  Mensagem,
  MensagemStatus,
  MessagingContext,
  NegociacaoAttachmentUploadResult,
} from '../types/messaging.types';
import { messagingService } from './services';
import {
  addMensagem,
  resetAttachmentUpload,
  setAttachmentUploadStatus,
  setConversas,
  setError,
  setLoadingConversas,
  setLoadingMensagens,
  setMensagens,
  setTotalMensagens,
  updateConversa,
  updateMensagemStatus,
} from './slice';

const resolveMessagingContext = (
  state: RootState,
  conversaId: string,
): { context: MessagingContext } | { error: string } => {
  const user = state.auth.user;
  if (!user?.empresaId) {
    return { error: 'Não autenticado' };
  }

  const conversa =
    (state.messaging.conversas ?? []).find((c: Conversa) => c.id === conversaId) ?? null;
  const caso = state.negociacaoDetail?.caso || null;

  const escritorioId =
    conversa?.escritorioId ??
    caso?.escritorioId ??
    state.escritorioCrud?.currentEscritorio?.id ??
    getStoredWorkspaceId() ??
    undefined;

  if (!escritorioId) {
    return { error: 'Workspace não selecionado' };
  }

  return { context: { empresaId: user.empresaId, escritorioId } };
};

export const negociacaoCasoStatusUpdated = createAction<{
  caseId: string;
  status: string;
  tipoEncerramento?: string | null;
}>('messaging/negociacaoCasoStatusUpdated');

export const direcionamentoPendenciaCreated = createAction<DirectionPendencia>(
  'messaging/direcionamentoPendenciaCreated',
);

export const direcionamentoResolved = createAction<{
  negociacaoCasoId: string;
  pendenciaId: string;
}>('messaging/direcionamentoResolved');

export const direcionamentoFailed = createAction<{
  negociacaoCasoId: string;
  pendenciaId: string;
}>('messaging/direcionamentoFailed');

export const fetchConversas = createAsyncThunk(
  'messaging/fetchConversas',
  async (
    params: { empresaId: string; escritorioId?: string; updatedSince?: string },
    { dispatch },
  ) => {
    dispatch(setLoadingConversas(true));
    try {
      const data = await messagingService.getConversas(params);
      dispatch(setConversas(data.items));
    } catch {
      dispatch(setError('Erro ao carregar conversas'));
    } finally {
      dispatch(setLoadingConversas(false));
    }
  },
);

export const refreshConversaByEntity = createAsyncThunk(
  'messaging/refreshConversaByEntity',
  async (
    params: { empresaId: string; escritorioId?: string; entityType: string; entityId: string },
    { dispatch },
  ) => {
    try {
      const data = await messagingService.getConversas({
        empresaId: params.empresaId,
        escritorioId: params.escritorioId,
        entityType: params.entityType,
        entityId: params.entityId,
        limit: 1,
        offset: 0,
      });

      const conversa = data.items?.[0] ?? null;
      if (conversa) {
        dispatch(updateConversa(conversa));
      }
    } catch {
      dispatch(setError('Erro ao atualizar conversa'));
    }
  },
);

export const fetchMensagens = createAsyncThunk(
  'messaging/fetchMensagens',
  async (params: { conversaId: string; updatedSince?: string }, { dispatch }) => {
    dispatch(setLoadingMensagens(true));
    try {
      const PAGE_SIZE = 100;
      let offset = 0;
      let allItems: Mensagem[] = [];
      let hasMore = true;
      let total = 0;

      while (hasMore) {
        const data = await messagingService.getMensagens({
          ...params,
          limit: PAGE_SIZE,
          offset,
        });

        allItems = allItems.concat(data.items);
        total = data.total;
        offset += PAGE_SIZE;
        hasMore = data.items.length === PAGE_SIZE && allItems.length < data.total;
      }

      dispatch(
        setMensagens({
          conversaId: params.conversaId,
          mensagens: allItems,
        }),
      );
      dispatch(setTotalMensagens({ conversaId: params.conversaId, total }));
    } catch {
      dispatch(setError('Erro ao carregar mensagens'));
    } finally {
      dispatch(setLoadingMensagens(false));
    }
  },
);

export const pollMensagens = createAsyncThunk(
  'messaging/pollMensagens',
  async (params: { conversaId: string }, { dispatch }) => {
    try {
      const data = await messagingService.getMensagens({
        conversaId: params.conversaId,
        limit: 100,
        offset: 0,
      });

      dispatch(
        setMensagens({
          conversaId: params.conversaId,
          mensagens: data.items,
        }),
      );
      dispatch(setTotalMensagens({ conversaId: params.conversaId, total: data.total }));
    } catch {
      dispatch(setError('Erro ao carregar mensagens'));
    }
  },
);

export const pauseConversaAutomation = createAsyncThunk(
  'messaging/pauseConversaAutomation',
  async (params: { conversaId: string }, { dispatch }) => {
    try {
      const conversa = await messagingService.updateConversa(params.conversaId, { mode: 'HUMAN' });
      dispatch(updateConversa(conversa));
    } catch {
      dispatch(setError('Erro ao pausar automação'));
    }
  },
);

export const resumeConversaAutomation = createAsyncThunk(
  'messaging/resumeConversaAutomation',
  async (params: { conversaId: string }, { dispatch }) => {
    try {
      const conversa = await messagingService.updateConversa(params.conversaId, { mode: 'BOT' });
      dispatch(updateConversa(conversa));
    } catch {
      dispatch(setError('Erro ao retomar automação'));
    }
  },
);

export const sendNegociacaoMessage = createAsyncThunk(
  'messaging/sendNegociacaoMessage',
  async (
    params: { conversaId: string; caseId: string; phone: string; text: string },
    { dispatch, getState },
  ) => {
    dispatch(setLoadingMensagens(true));
    try {
      const resolved = resolveMessagingContext(getState() as RootState, params.conversaId);
      if ('error' in resolved) {
        dispatch(setError(resolved.error));
        return false;
      }
      const { empresaId, escritorioId } = resolved.context;

      const created = await messagingService.createMensagem({
        empresaId,
        escritorioId,
        conversaId: params.conversaId,
        direction: 'OUTBOUND',
        canal: 'WHATSAPP',
        tipo: 'TEXT',
        textBody: params.text,
        status: 'PENDING',
        metadata: { isManual: true, caseId: params.caseId },
      });

      dispatch(addMensagem(created));

      try {
        const result = await messagingService.sendNegotiacaoMessage({
          phone: params.phone,
          message: params.text,
          type: 'text',
          metadata: {
            caseId: params.caseId,
            empresaId,
            escritorioId,
          },
        });

        if (result.success) {
          dispatch(
            updateMensagemStatus({
              id: created.id,
              conversaId: params.conversaId,
              status: 'SENT',
            }),
          );

          const patch: { status: MensagemStatus; providerMessageId?: string } = {
            status: 'SENT',
          };
          if (result.messageId) {
            patch.providerMessageId = result.messageId;
          }
          await messagingService.updateMensagem(created.id, patch);
          return true;
        } else {
          throw new Error('Erro ao enviar mensagem');
        }
      } catch {
        dispatch(
          updateMensagemStatus({
            id: created.id,
            conversaId: params.conversaId,
            status: 'FAILED',
          }),
        );
        await messagingService.updateMensagem(created.id, { status: 'FAILED' });
        dispatch(setError('Erro ao enviar mensagem'));
        return false;
      }
    } finally {
      dispatch(setLoadingMensagens(false));
    }
  },
);

export const uploadAndSendNegociacaoAttachment = createAsyncThunk(
  'messaging/uploadAndSendNegociacaoAttachment',
  async (
    params: {
      conversaId: string;
      caseId: string;
      phone: string;
      file: File;
      caption?: string;
      shouldPauseIa?: boolean;
    },
    { dispatch, getState, rejectWithValue },
  ) => {
    const resolved = resolveMessagingContext(getState() as RootState, params.conversaId);
    if ('error' in resolved) {
      dispatch(setAttachmentUploadStatus({ status: 'error', error: resolved.error }));
      return rejectWithValue(resolved.error);
    }
    const { empresaId, escritorioId } = resolved.context;

    const caption = params.caption?.trim() ?? '';
    const tipo: Mensagem['tipo'] = params.file.type.startsWith('image/') ? 'IMAGE' : 'DOCUMENT';
    const type: 'image' | 'document' = tipo === 'IMAGE' ? 'image' : 'document';

    dispatch(setAttachmentUploadStatus({ status: 'uploading', error: null }));

    let uploaded: NegociacaoAttachmentUploadResult;
    try {
      uploaded = await messagingService.uploadNegociacaoAttachment(params.caseId, params.file);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erro ao enviar anexo';
      dispatch(setAttachmentUploadStatus({ status: 'error', error: message }));
      return rejectWithValue(message);
    }

    if (params.shouldPauseIa) {
      dispatch(pauseConversaAutomation({ conversaId: params.conversaId }));
    }

    let created: Mensagem;
    try {
      created = await messagingService.createMensagem({
        empresaId,
        escritorioId,
        conversaId: params.conversaId,
        direction: 'OUTBOUND',
        canal: 'WHATSAPP',
        tipo,
        textBody: caption,
        mediaUrl: uploaded.mediaUrl,
        status: 'PENDING',
        metadata: { isManual: true, caseId: params.caseId, filename: uploaded.filename },
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erro ao registrar o anexo';
      dispatch(setAttachmentUploadStatus({ status: 'error', error: message }));
      return rejectWithValue(message);
    }

    dispatch(addMensagem(created));
    dispatch(setAttachmentUploadStatus({ status: 'sending', error: null }));

    try {
      const result = await messagingService.sendNegotiacaoMessage({
        phone: params.phone,
        message: caption,
        mediaUrl: uploaded.mediaUrl,
        type,
        metadata: {
          caseId: params.caseId,
          empresaId,
          escritorioId,
          filename: uploaded.filename,
        },
      });

      if (!result.success) {
        throw new Error('Erro ao enviar anexo');
      }

      dispatch(
        updateMensagemStatus({ id: created.id, conversaId: params.conversaId, status: 'SENT' }),
      );

      const patch: { status: MensagemStatus; providerMessageId?: string } = { status: 'SENT' };
      if (result.messageId) {
        patch.providerMessageId = result.messageId;
      }
      await messagingService.updateMensagem(created.id, patch);

      dispatch(resetAttachmentUpload());
    } catch (err) {
      dispatch(
        updateMensagemStatus({ id: created.id, conversaId: params.conversaId, status: 'FAILED' }),
      );
      await messagingService.updateMensagem(created.id, { status: 'FAILED' });
      const message = err instanceof Error ? err.message : 'Erro ao enviar anexo';
      dispatch(setAttachmentUploadStatus({ status: 'error', error: message }));
      return rejectWithValue(message);
    }
  },
);

export const catchUpMessaging = createAsyncThunk(
  'messaging/catchUp',
  async (_, { dispatch, getState }) => {
    const state = getState() as RootState;
    const { lastEventAt } = state.messaging;

    if (!lastEventAt) return;

    try {
      const data = await messagingService.catchUpConversas(lastEventAt);
      const updatedConversas = data.items;

      for (const conversa of updatedConversas) {
        dispatch(updateConversa(conversa));
      }
    } catch (error) {
      console.error('Erro no catch-up:', error);
    }
  },
);
